/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import { isAntiBotError } from '@services/requests/core/request-error-guards';

export type SaveErrorKind = 'antibot' | 'validation' | 'generic';

interface ErrorLike {
  name?: string;
  status?: number;
  body?: unknown;
  response?: { status?: number; data?: unknown };
}

/**
 * The HTTP status of an ApiError or axios error.
 *
 * @param error - The error-like value.
 * @returns The status, if any.
 */
function statusOf(error: ErrorLike): number | undefined {
  return error.status ?? error.response?.status;
}

/**
 * The response body of an ApiError or axios error.
 *
 * @param error - The error-like value.
 * @returns The body, if any.
 */
function bodyOf(error: ErrorLike): unknown {
  return error.body ?? error.response?.data;
}

/**
 * Classifies why a save failed, so an anti-bot rejection (which reaches us as a
 * 400 `AntiBotTokenMissingError`, a 428, or a cancelled challenge) is not
 * reported as "invalid data".
 *
 * @param error - The thrown value.
 * @returns `antibot`, `validation` (plain 400) or `generic`.
 */
export function saveErrorKind(error: unknown): SaveErrorKind {
  const err = (error ?? {}) as ErrorLike;
  const status = statusOf(err);
  if (err.name === 'AntiBotChallengeCancelledError' || status === 428 || isAntiBotError(bodyOf(err))) {
    return 'antibot';
  }
  return status === 400 ? 'validation' : 'generic';
}
